<template>
    <b-modal
            :id="modalId"
            :visible="show"
            @hidden="hideEvents"
            :title="title"
            scrollable
    >
        <vue-element-loading :active="loading" :is-full-screen="false"/>
        <b-alert :show="!hasOptions && !loading"
                 variant="danger"
        >
            There are currently no managers to send this query to.
        </b-alert>
        <b-form v-if="hasOptions">
            <b-form-group
                    label="Send query to:"
                    label-size="sm"
            >
                <b-input-group>
                    <assign-select
                            v-if="options.length > 0"
                            :options="options"
                            v-model="form.recipient"
                    >
                    </assign-select>
                    <div v-else-if="form.recipient !== null"
                         class="b-form-group-content">
                        {{ form.recipient.firstName + ' ' + form.recipient.lastName }}
                    </div>
                </b-input-group>
            </b-form-group>

            <slot></slot>

            <b-form-textarea
                    :state="getState($v.form.query)"
                    class="mb-3"
                    placeholder="Write a query..."
                    rows="6"
                    v-model="$v.form.query.$model"
            >
            </b-form-textarea>

            <vue-dropzone
                    v-if="url"
                    id="fileDrop"
                    ref="dropzone"
                    :options="queryOptions"
                    @vdropzone-sending-multiple="attachParams"
                    @vdropzone-success-multiple="onFileSuccess"
                    @vdropzone-error="onFileError"
                    @vdropzone-file-added="checkForDuplicates"
            >
            </vue-dropzone>

            <form-error v-if="$v.form.$error">
                Please enter a query.
            </form-error>
        </b-form>
        <template slot="modal-footer">
            <b-button
                    @click="hideModal"
                    variant="outline-secondary">
                Cancel
                <font-awesome-icon
                        :icon="['far', 'times']">
                </font-awesome-icon>
            </b-button>
            <b-button
                    :disabled="disabled"
                    @click="submitQuery"
                    class="btn-arrow"
                    variant="yellow"
            >
                <span v-if="isBulk">Submit Queries</span>
                <span v-else>Submit Query</span>
                <font-awesome-icon
                        :icon="['far', 'arrow-right']">
                </font-awesome-icon>
            </b-button>
        </template>
    </b-modal>
</template>

<script>
import CaretIcon from '@/components/Form/CaretIcon';
import ModalMixin from '@/components/Modal/ModalMixin';
import FormValidate from '@/services/Mixins/FormValidate';
import { required } from 'vuelidate/lib/validators';
import AssignSelect from '@/components/Form/AssignSelect';
import FileDropMixin from '@/components/Form/FileDropMixin';
import ActionStatus from '@/services/Action/ActionStatus';
import ErrorMixin from '@/components/Notifications/ErrorMixin';
import api from '@/api';
import { singleRequestUploadOptions } from '@/components/Form/singleRequestUpload';
import ClientAction from '@/services/Action/ClientAction';

export default {
  name: 'Submit-Query-Modal',
  components: {
    CaretIcon,
    AssignSelect,
  },
  props: {
    clientActionId: {
      required: true,
    },
    isBulk: {
      type: Boolean,
      required: false,
      default: false,
    },
    type: {
      required: false,
    },
    optionsProp: {
      default: () => [],
    },
    model: {
      required: false,
    },
  },
  mixins: [ModalMixin, FormValidate, ErrorMixin, FileDropMixin],
  data() {
    return {
      modalId: 'submitQueryModal',
      loading: false,
      options: this.optionsProp || [],
      form: {
        query: '',
        recipient: null,
      },
    };
  },
  validations: {
    form: {
      query: {
        required,
      },
    },
  },
  computed: {
    disabled() {
      return this.$v.form.$invalid || this.loading;
    },
    hasOptions() {
      return this.options.length > 0 || this.form.recipient;
    },
    url() {
      if (this.getModel() === ClientAction) {
        return false;
      }

      if (this.isBulk) {
        return `${api.defaults.baseURL}/actions/riskhub/query/bulk`;
      }

      return `${api.defaults.baseURL}/actions/riskhub/${this.clientActionId}/status/query`;
    },
    title() {
      return this.isBulk ? 'Submit queries' : 'Submit a query';
    },
    queryOptions() {
      return {
        ...this.dropOptions,
        ...singleRequestUploadOptions,
        url: this.url,
      };
    },
  },
  created() {
    this.setOptions();
  },
  methods: {
    async submitQuery() {
      const { query, recipient } = this.form;

      const hasNotSelectedUser = !recipient || !recipient.userId || !recipient.teamId;
      if (hasNotSelectedUser) {
        this.notifyError('Please select who the the query is intended for', 'Query recipient required');
        this.loading = false;
        return false;
      }

      this.loading = true;

      // File route, post via dropzone
      if (this.url && this.hasFiles()) {
        this.processFiles();

        return true;
      }

      const params = {
        clientActionId: this.clientActionId,
        query,
      };

      if (recipient) {
        params.teamId = Number(recipient.teamId);

        if (recipient.userId) {
          params.userId = Number(recipient.userId);
        }
      }

      if (this.isBulk) {
        this.$emit('submit-bulk-query', params);
        return true;
      }

      try {
        const model = this.getModel();
        await model.submitQuery(params);

        this.notifySuccess();
        this.loading = false;
        this.hideModal();

        return true;
      } catch (e) {
        this.showValidationError(e);
      } finally {
        this.loading = false;
      }
    },

    notifySuccess() {
      this.$store.dispatch(
        'updateActionField',
        {
          field: 'status',
          value: ActionStatus.ON_HOLD(),
        },
      );

      this.$notify({
        duration: 7000,
        title: 'Action query submitted',
        text: 'Action\'s query has been submitted.',
        type: 'success',
      });

      this.$store.dispatch('refreshAction');
      this.$store.dispatch('refreshActions');

      this.$emit('submitted', true);
    },

    onFileSuccess(file, response) {
      this.notifySuccess();
      this.loading = false;
      this.hideModal();
    },

    attachParams(files, xhr, formData) {
      if (this.isBulk) {
        this.clientActionId.forEach((id) => {
          formData.append('actionIds[]', id);
        });
      } else {
        formData.append('clientActionId', this.clientActionId);
      }

      const { query, recipient } = this.form;

      formData.append('query', query);
      formData.append('teamId', Number(recipient.teamId));
      formData.append('userId', Number(recipient.userId));
    },

    async setOptions() {
      this.loading = true;
      try {
        const model = this.getModel();
        const response = await model.getTeamManagers(this.clientActionId);

        const { managers, reviewer } = response.data.data;

        const options = [];

        if (reviewer) {
          const reviewerOptions = {};

          if (reviewer.userId) {
            reviewerOptions.label = `${reviewer.firstName} ${reviewer.lastName}`;
            reviewerOptions.userId = reviewer.userId;
            reviewerOptions.user = reviewer.userId;
            reviewerOptions.teamId = reviewer.teamId;

            options.push({
              team: 'Assignee',
              items: [
                reviewerOptions,
              ],
            });
          }
        }

        if (managers.length > 0) {
          // If there is no reviewer option AND only one manager present
          // We make it autoselected
          if (options.length === 0 && managers.length === 1) {
            const onlyManager = managers[0];
            onlyManager.teamId = onlyManager.team.teamId;
            this.form.recipient = onlyManager;
            return;
          }

          const items = [];
          managers.forEach((manager) => {
            const { team } = manager;

            if (!items[team.teamId]) {
              items[team.teamId] = [];
            }

            items[team.teamId].push({
              label: `${manager.firstName} ${manager.lastName}`,
              userId: manager.userId,
              user: manager.userId,
              teamId: team.teamId,
              team: team.team,
            });
          });

          items.forEach((teamManagers) => {
            const name = teamManagers[0].team;
            options.push({
              team: `${name} managers`,
              items: teamManagers,
            });
          });
        }

        this.options = options;
      } catch (e) {
      } finally {
        this.loading = false;
      }
    },

    hideModalEvents() {
      this.form.query = '';
      this.form.recipient = null;
    },

    getModel() {
      if (this.model) {
        return this.model;
      }

      return this.$route.meta.model;
    },
  },
};
</script>

<style lang="scss">
    .dz-progress {
        display: none;
    }

    .vue-dropzone .dz-preview {
        .dz-remove {
            top: 0;
            right: 0;
            opacity: 1;
            background: rgba(78, 115, 138, 0.95);
            bottom: initial;
            padding: 5px 7.5px;
            border: 0;
        }
    }
</style>
